import type { StsIconName } from '@packages/ui/shared';

import { colors } from '@packages/core/styles';
import { Button, Icon, Typography } from '@packages/ui/shared';
import { useCallback } from 'react';
import { Dropdown } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';
import { StyleSheet } from 'react-native';

import { DropdownButtonToggle } from './dropdown-button-toggle';

/** One entry of the menu. */
export type DropdownButtonItem<TKey extends string> = {
    /** Identifies the entry to the owner. */
    key: TKey;
    /** The text of the entry. */
    label: string;
};

/** Props for {@link DropdownButton}. */
export type DropdownButtonProps<TKey extends string> = {
    /** The icon before the title, when the button has one. */
    icon?: StsIconName;
    /** The icon after the title; the small caret unless the caller asks for another. */
    iconRight?: StsIconName;
    /** The id of the toggle, which ties the menu to its button for assistive technology. */
    id: string;
    /** Whether the button is off and its menu stays closed. */
    isDisabled?: boolean;
    /** Whether the menu is open. */
    isOpen: boolean;
    /** The entries of the menu, in display order. */
    items: ReadonlyArray<DropdownButtonItem<TKey>>;
    /** Receives the key of the chosen entry. */
    onSelect: (key: TKey) => void;
    /** Receives the open state the button or the menu asks for. */
    onToggle: (isOpen: boolean) => void;
    /** The title of the button. */
    title: string;
};

const styles = StyleSheet.create({
    button: { borderRadius: 4, paddingHorizontal: 7 },
    disabledButton: { borderColor: colors.brandDisabled, opacity: 1 },
    disabledTitle: { color: colors.brandDisabled },
    icon: { marginLeft: 0, marginRight: 0 },
    item: { fontSize: 15, fontWeight: '400', lineHeight: 24 },
    title: { fontWeight: '600', paddingHorizontal: 8 },
});

/** The enabled and disabled looks of the button and its icons. */
const BUTTON_LOOKS = {
    disabled: {
        color: 'brandDisabled' as const,
        style: [styles.button, styles.disabledButton],
        titleStyle: [styles.title, styles.disabledTitle],
    },
    enabled: { color: 'blueOne' as const, style: styles.button, titleStyle: styles.title },
};

/** The size of the button icons. */
const ICON_SIZE = 24;

/** The icon after the title unless the caller names another. */
const DEFAULT_ICON_RIGHT: StsIconName = 'arrow-small-down';

const useStyles = createUseStyles({
    menu: {
        '&&&': {
            backgroundColor: colors.white,
            border: 0,
            borderRadius: 4,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.16)',
            padding: 0,
            width: 256,
        },
    },
    menuItem: {
        '&&&': { color: colors.black, height: 48, padding: '12px 16px' },
        '&&&:active, &&&:focus, &&&:hover': { backgroundColor: colors.grayOne, color: colors.black },
    },
    toggle: {
        '&&': { backgroundColor: 'transparent', border: 0, padding: 0 },
        '&::after': { display: 'none' },
    },
});

/**
 * A controlled button and menu that hand state changes and selected keys to their owner.
 * The wrapper hands the library its focusable button for focus return and menu positioning.
 * The menu and its entries carry the menu roles the button promises assistive technology.
 * The button follows the design system's anatomy: 8px around, 24px icons without margins and 8px
 * at each side of the title.
 *
 * @param props - {@link DropdownButtonProps}
 * @returns the button and its menu
 */
export function DropdownButton<TKey extends string>({
    icon,
    iconRight = DEFAULT_ICON_RIGHT,
    id,
    isDisabled = false,
    isOpen,
    items,
    onSelect,
    onToggle,
    title,
}: DropdownButtonProps<TKey>) {
    const classes = useStyles();
    const look = BUTTON_LOOKS[isDisabled ? 'disabled' : 'enabled'];
    const isMenuOpen = isDisabled ? false : isOpen;
    const iconLeft = icon === undefined ? undefined : <Icon color={look.color} name={icon} size={ICON_SIZE} />;

    const changeOpen = useCallback(
        (nextOpen: boolean) => onToggle(isDisabled ? false : nextOpen),
        [isDisabled, onToggle]
    );
    const toggle = useCallback(() => changeOpen(!isOpen), [changeOpen, isOpen]);
    const select = useCallback(
        (key: string | null) => {
            const item = items.find((candidate) => candidate.key === key);

            if (item !== undefined) {
                onSelect(item.key);
            }
        },
        [items, onSelect]
    );

    return (
        <Dropdown onSelect={select} onToggle={changeOpen} show={isMenuOpen}>
            <Dropdown.Toggle as={DropdownButtonToggle} className={classes.toggle} id={id}>
                <Button
                    accessibilityExpanded={isMenuOpen}
                    accessibilityHasPopup
                    disabled={isDisabled}
                    iconLeft={iconLeft}
                    iconLeftStyle={styles.icon}
                    iconRight={<Icon color={look.color} name={iconRight} size={ICON_SIZE} />}
                    iconRightStyle={styles.icon}
                    nativeID={id}
                    onPress={toggle}
                    style={look.style}
                    title={title}
                    titleStyle={look.titleStyle}
                    variant="primary-blue-outline"
                />
            </Dropdown.Toggle>
            <Dropdown.Menu align="right" className={classes.menu} role="menu">
                {items.map((item) => (
                    <Dropdown.Item className={classes.menuItem} eventKey={item.key} key={item.key} role="menuitem">
                        <Typography color="black" style={styles.item} variant="default">
                            {item.label}
                        </Typography>
                    </Dropdown.Item>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    );
}
